import { BonusSettlementType, getBonusSettlementTranslationId } from '@synergy/models';
import { Box, Chip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PaloActivityDock, PaloStatusPill } from '@synergy/components-palo';
import { ReactElement } from 'react';
import { RouteTab, RouteTabs } from '@synergy/components-next';

import { BonusSettlementCustomerOverviewFilter } from './customer-overview/bonus-settlement-customer-overview-filter';
import { BonusSettlementSupplierOverviewDetailSidebar } from './supplier-overview/components/bonus-settlement-supplier-overview-detail-sidebar';
import { BonusSettlementSupplierOverviewFilter } from './supplier-overview/components/bonus-settlement-supplier-overview-filter';
import { BonusSettlementTab } from './models/bonus-settlement-tab';
import { CustomerHeaderActions } from './header/customer-header-actions';
import { PaloBonusSettlementCustomerList } from './customer-overview/palo/palo-bonus-settlement-customer-list';
import { PaloBonusSettlementSupplierList } from './supplier-overview/palo/palo-bonus-settlement-supplier-list';
import { PaloOverview } from '../../../common/components/palo/palo-overview';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { SupplierHeaderActions } from './header/supplier-header-actions';
import { bonusSettlementsCustomerOverviewDetailsSelector } from './customer-overview/store/bonus-settlements-customer-overview-selector';
import {
    bonusSettlementsSupplierOverviewDetailsSelector,
    bonusSettlementsSupplierOverviewRequestingSelector,
} from './supplier-overview/store/bonus-settlements-supplier-overview-selector';
import { resetBonusSettlementsSupplierOverviewState } from './supplier-overview/store/bonus-settlements-supplier-overview-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export const BonusSettlementDetail = (): ReactElement | null => {
    const dispatch = useThunkDispatch();
    const requesting = useAppSelector(bonusSettlementsSupplierOverviewRequestingSelector);
    const supplierOverviewDetails = useAppSelector(bonusSettlementsSupplierOverviewDetailsSelector);
    const customerOverviewDetails = useAppSelector(bonusSettlementsCustomerOverviewDetailsSelector);
    const location = useLocation();
    const navigate = useNavigate();

    const { id: bonusSettlementId } = useParams<{ id: string }>();
    const currentTab = (location.pathname.split('/').pop() as BonusSettlementTab) ?? BonusSettlementTab.Supplier;

    const handleReset = () => {
        dispatch(resetBonusSettlementsSupplierOverviewState());
    };

    const details = currentTab === BonusSettlementTab.Supplier ? supplierOverviewDetails : customerOverviewDetails;

    return (
        <PaloOverview
            dock={
                <PaloActivityDock
                    defaultOpen={false}
                    panels={[
                        {
                            key: 'details',
                            label: <FormattedMessage id="app.common.details" />,
                            content: <BonusSettlementSupplierOverviewDetailSidebar selectedTab={currentTab} />,
                        },
                    ]}
                />
            }
            titleMessage={
                <Box
                    component="span"
                    color={supplierOverviewDetails?.type === BonusSettlementType.Reclaim ? 'error.main' : undefined}
                >
                    <FormattedMessage
                        id={
                            supplierOverviewDetails?.type === BonusSettlementType.Reclaim
                                ? 'app.bonusSettlementsSupplierOverview.title.reclamation'
                                : 'app.bonusSettlementsSupplierOverview.title'
                        }
                        values={{ year: details?.year }}
                    />
                </Box>
            }
            requesting={requesting}
            onResetData={handleReset}
            filters={
                currentTab === BonusSettlementTab.Supplier ? (
                    <BonusSettlementSupplierOverviewFilter />
                ) : (
                    <BonusSettlementCustomerOverviewFilter />
                )
            }
            breadcrumbs={[
                {
                    label: <FormattedMessage id="app.navigation.bonusSettlements" />,
                    onClick: () => navigate(ROUTES.bonusSettlements.overview),
                },
            ]}
            additionalItemsRight={[
                // Next to the status, not inside the title: in the top bar the
                // title is the part that gives way, and the quota was the
                // first thing to be cut off.
                <Chip
                    key="payout"
                    size="small"
                    sx={{ flexShrink: 0 }}
                    label={
                        <FormattedMessage
                            id="app.bonusSettlementsSupplierOverview.payout"
                            values={{ payout: details?.payout }}
                        />
                    }
                />,
                details?.status !== undefined && (
                    <PaloStatusPill
                        key="status"
                        status={details.status}
                        label={<FormattedMessage id={getBonusSettlementTranslationId(details.status, details.type)} />}
                    />
                ),
                bonusSettlementId && currentTab === BonusSettlementTab.Supplier && (
                    <SupplierHeaderActions key="supplierActions" bonusSettlementId={+bonusSettlementId} />
                ),
                bonusSettlementId && currentTab === BonusSettlementTab.Customer && (
                    <CustomerHeaderActions key="customerActions" bonusSettlementId={+bonusSettlementId} />
                ),
            ]}
            tabs={[
                <RouteTabs tabs={BonusSettlementTab} indicatorColor="primary">
                    <RouteTab
                        value={BonusSettlementTab.Supplier}
                        label={<FormattedMessage id={'app.bonusSettlements.tab.supplier'} />}
                    />
                    <RouteTab
                        value={BonusSettlementTab.Customer}
                        label={<FormattedMessage id="app.bonusSettlements.tab.customer" />}
                    />
                </RouteTabs>,
            ]}
        >
            <Routes>
                <Route path={BonusSettlementTab.Supplier} element={<PaloBonusSettlementSupplierList />} />
                <Route path={BonusSettlementTab.Customer} element={<PaloBonusSettlementCustomerList />} />
                <Route
                    index={true}
                    element={<Navigate to={{ pathname: BonusSettlementTab.Supplier }} replace={true} />}
                />
            </Routes>
        </PaloOverview>
    );
};
